import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import { match } from 'ts-pattern'

import { isStatusSuccessful } from '@frame/ui-components/direct-task-card/utils'

import { VerificationType } from '@frame/lib/api/client-session/types'
import { InternalEventType } from '@frame/lib/api/events/types'
import { buildEmployerHistoryViewProps } from '@frame/lib/api/events/utils'
import { DirectTaskType, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import * as errors from '@frame/lib/errors'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useLoginItems } from '@frame/lib/hooks/useLoginItems'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { useStore } from '@frame/lib/stores/useStore'
import { addStatusToTasks } from '@frame/lib/utils/tasks'
import { routes } from '@frame/routes'

import { Loading } from '../non-terminal/loading'
import { useVerifyTask } from './hooks/useVerifyTask'
import { EmploymentIncomeTasks } from './screens/employment-income-tasks'
import { IncomeOnlyTasks } from './screens/income-only-tasks'
import { TenantScreeningTasks } from './screens/tenant-screening-tasks'

export function Tasks() {
  const { redirectToSessionStateRoute } = useAutoNavigate()
  const { handleEvent } = useEventHandler()
  const { handleConnectBank } = useVerifyTask()
  const setHasLoggedIntoAtLeastOneEmployment = useStore(
    ({ setHasLoggedIntoAtLeastOneEmployment }) => setHasLoggedIntoAtLeastOneEmployment
  )
  const { docsForMortgageEnabled } = useStore(({ session }) => ({
    docsForMortgageEnabled: session?.configuration.docs_for_mortgage_enabled ?? false,
  }))
  const navigate = useNavigate()

  const { api } = useRootContext()
  const { documentsEnabled, credentialsEnabled, irsEnabled, bankEnabled, bilEnabledStrategies } = useStrategies()
  const loginItemIds = useLoginItems()

  const { reportType, maxEmployments, setError } = useStore(({ session, setError }) => ({
    reportType: session?.verification_type,
    maxEmployments: session?.max_employments ?? 1,
    setError,
  }))

  const { data, isLoading, isValidating } = useSWR(['all', 'tasks'], api.tasks.getAllDirectTasks, {
    keepPreviousData: true,
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data) => {
      if ((!data.data && !data.response.ok) || data.error) {
        errors.create({
          error: errors.getErrorWithFallback(data.error, new Error('Failed to load data for getAllEmployments')),
          code: errors.codes.Network,
          extra: errors.parseExtra(data.error),
          displayReason: 'Failed to start verification',
          setError,
        })
        return
      }
    },
  })

  const tasks = useMemo(() => data?.data ?? [], [data])
  const isLoadingTasks = isLoading || isValidating
  const canAddEmployer = tasks.length < maxEmployments && !loginItemIds.length

  // incomeTasks are tasks that are not tied to an existing employer/employment. can be for any report type.
  const incomeTasks = tasks.filter((task) => task.task_type === DirectTaskType.VERIFY_ASSETS)

  // employmentTasks already have a specified employer. can be for any report type.
  const employmentTasks = tasks.filter((task) => task.task_type !== DirectTaskType.VERIFY_ASSETS)

  useEffect(() => {
    const tasksWithStatus = addStatusToTasks({
      directTasks: tasks,
      credentialsEnabled,
      documentsEnabled,
      bankEnabled,
      irsEnabled,
      docsForMortgageEnabled,
    })
    const successfulTasks = [...tasksWithStatus.filter((task) => isStatusSuccessful(task.visualStatus))]
    setHasLoggedIntoAtLeastOneEmployment(successfulTasks.length > 0)
  }, [tasks]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    redirectToSessionStateRoute()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleEvent(
      InternalEventType.EMPLOYER_HISTORY_VIEWED,
      buildEmployerHistoryViewProps(canAddEmployer, bilEnabledStrategies, tasks)
    )
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (bankEnabled && tasks.length === 1 && employmentTasks[0]?.strategy_info?.show_employee_company_match_picker) {
      // If the task is complete then we need to redirect to the complete screen
      if (employmentTasks[0]?.strategy === TrueworkJsStrategy.CREDENTIALS) {
        navigate(`${routes.bankConnectedTasks}/${employmentTasks[0].id}`)
        return
      }
      handleConnectBank(employmentTasks[0])
    }
  }, [handleConnectBank, employmentTasks]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoadingTasks) {
    return <Loading />
  }

  return match(reportType)
    .with(VerificationType.TENANT_SCREENING, () => <TenantScreeningTasks tasks={tasks} />)
    .with(VerificationType.INCOME_ONLY, () => (
      <IncomeOnlyTasks incomeTasks={incomeTasks} employmentTasks={employmentTasks} />
    ))
    .otherwise(() => <EmploymentIncomeTasks tasks={employmentTasks} />)
}
